import { RefObject, useState } from 'react';
import {
  Box, Button, IconButton, Popover,
  useMediaQuery, useTheme,
  Paper, Tooltip,
} from '@mui/material';
import { Add, Download, Refresh, ViewColumn, SwapHoriz, CompareArrows } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { downloadGridCsv } from '../../utils/csv-export';

import DateRangePicker from '../../components/DateRangePicker';
import DataFreshness from '../../components/DataFreshness';
import TimezoneSelect from '../../components/TimezoneSelect';
import FiltersButton from './FiltersButton';
import EntityFilterPicker from '../../components/EntityFilterPicker';
import PresetBar from './PresetBar';
import { MAX_DRILLDOWN_LEVELS, levelDims, setLevelAt, usedElsewhere } from './tracking-page.format';
import LevelPicker from '../../components/LevelPicker';

// Sized to the chips: the search input only takes room while focused.
const PICKER_SX = {
  minWidth: { xs: 0, md: 110 }, maxWidth: { md: 240 }, flex: { xs: '1 1 100%', md: '0 1 auto' },
  '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
  '&:not(.Mui-focused) .MuiAutocomplete-input': { minWidth: '4px !important', width: '4px', px: '0 !important' },
} as const;
const POPOVER_PICKER_SX = { '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } } as const;
import { ReportState } from './useReportState';

interface Props {
  report: ReportState;
  /** Measured by useGridHeight so the grid fills whatever the toolbar leaves. */
  containerRef: RefObject<HTMLDivElement>;
  /** Field label using the traffic source's real v-names when one is filtered. */
  vLabel: (field: string) => string;
  rows: Record<string, any>[];
  columns: GridColDef[];
  onRefresh: () => void;
  onPickColumns: () => void;
}

export default function TrackingToolbar({ report, containerRef, vLabel, rows, columns, onRefresh, onPickColumns }: Props) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const {
    groupBy, setGroupBy, drilldownChain, setDrilldownChain,
    filters, setFilters, boxField, setPickedField,
    tagIds, setTagIds, 
    timezone, setTimezone, dateRange, setDateRange,
    compare, toggleCompare,
  } = report;
  const [adding, setAdding] = useState(false);
  const isWide = useMediaQuery(theme.breakpoints.up('xl'));
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  // Small laptops keep one drilldown inline and the rest in a popover; wide screens and phones (rows wrap) show all.
  const inline = isNarrow || isWide ? MAX_DRILLDOWN_LEVELS : 1;
  const shown = Math.min(drilldownChain.length, inline);
  const folded = !isNarrow && !isWide && drilldownChain.length >= inline;

  const entityFilter = (
    <EntityFilterPicker filters={filters} onFilters={setFilters} field={boxField} onField={setPickedField} labelForField={vLabel} />
  );

  // The action cluster, rendered in the mobile rows AND in the desktop row.
  const actionButtons = (
    <>
      <FiltersButton tagIds={tagIds} onTags={setTagIds} />

      <Tooltip title="Export CSV">
        <span>
          <IconButton
            size="small"
            disabled={!rows.length}
            onClick={() => downloadGridCsv(rows, columns, 'tracking-export.csv')}
          >
            <Download fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={compare ? 'Comparing with the previous period' : 'Compare with the previous period'}>
        {/* The name stays put and the state rides on aria-pressed: a control that renames
            itself when toggled is one a screen reader announces as a different button. */}
        <IconButton size="small" onClick={toggleCompare}
          aria-label="Compare with the previous period" aria-pressed={compare}
          color={compare ? 'primary' : 'default'}>
          <CompareArrows fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Choose columns">
        <IconButton size="small" onClick={onPickColumns}>
          <ViewColumn fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Wider than an icon; keep it off the cramped mobile header. */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
        <DataFreshness />
      </Box>

      <Tooltip title="Refresh">
        <IconButton size="small" onClick={onRefresh}>
          <Refresh fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Paper ref={containerRef} variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>

      {/* Row 1: grouping shortcut buttons (scrollable) + Group-by dropdown + Drilldown */}
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5, rowGap: 1,
        flexWrap: { xs: 'wrap', md: 'nowrap' }, mb: 1.5 }}>
        <PresetBar report={report} vLabel={vLabel} />

        {/* Group-by + Drilldown — fixed, never scroll away. Both share the same full
            column list (Drilldown just adds "None"). */}
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5, rowGap: 1,
          flexWrap: { xs: 'wrap', md: 'nowrap' }, flexShrink: 0,
          width: { xs: '100%', md: 'auto' } }}>
          <LevelPicker
            label="Group by" level={groupBy} required dimLabel={vLabel} sx={PICKER_SX} limitTags={1}
            used={drilldownChain.flatMap(levelDims)}
            onChange={setGroupBy}
          />

          <Tooltip title="Swap">
            <IconButton
              size="small"
              onClick={() => {
                setGroupBy(drilldownChain[0] || groupBy);
                setDrilldownChain([groupBy, ...drilldownChain.slice(1)]);
              }}
              sx={{ flexShrink: 0, p: 0.25, mx: -0.25, display: { xs: 'none', md: 'flex' } }}
            >
              <SwapHoriz fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: { xs: 'wrap', md: 'nowrap' },
            flex: { xs: '1 1 100%', md: '0 0 auto' }, minWidth: 0 }}>
            {drilldownChain.slice(0, shown).map((level, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                flex: { xs: '1 1 100%', md: '0 0 auto' }, minWidth: 0 }}>
                <LevelPicker
                  label={i === 0 ? 'Drilldown' : `Drilldown ${i + 1}`} level={level} dimLabel={vLabel} sx={PICKER_SX} limitTags={1}
                  used={usedElsewhere(groupBy, drilldownChain, i)}
                  onChange={(next) => setDrilldownChain((prev) => setLevelAt(prev, i, next))}
                />
                {i + 1 < shown && (
                  <Tooltip title="Swap">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newChain = [...drilldownChain];
                        [newChain[i], newChain[i + 1]] = [newChain[i + 1], newChain[i]];
                        setDrilldownChain(newChain);
                      }}
                      sx={{ flexShrink: 0, p: 0.25, mx: -0.25, display: { xs: 'none', md: 'flex' } }}
                    >
                      <SwapHoriz fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
            {folded && (
              <>
                <Tooltip title={shown < drilldownChain.length
                  ? drilldownChain.slice(shown).map((l) => levelDims(l).map(vLabel).join(' × ')).join(' › ')
                  : 'Add a drilldown'}>
                  <Button size="small" variant="outlined" aria-label="More drilldowns" onClick={(e) => setMoreAnchor(e.currentTarget)}
                    sx={{ minWidth: 0, flexShrink: 0 }}>
                    {shown < drilldownChain.length ? `+${drilldownChain.length - shown}` : <Add fontSize="small" />}
                  </Button>
                </Tooltip>
                <Popover open={!!moreAnchor} anchorEl={moreAnchor} onClose={() => setMoreAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, width: 260 }}>
                    {drilldownChain.slice(shown).map((level, j) => {
                      const i = shown + j;
                      return (
                        <LevelPicker key={i} label={`Drilldown ${i + 1}`} level={level} dimLabel={vLabel} sx={POPOVER_PICKER_SX}
                          used={usedElsewhere(groupBy, drilldownChain, i)}
                          onChange={(next) => {
                            setDrilldownChain((prev) => setLevelAt(prev, i, next));
                            if (!next) setMoreAnchor(null);
                          }}
                        />
                      );
                    })}
                    {drilldownChain.length < MAX_DRILLDOWN_LEVELS && (
                      <LevelPicker label={`Drilldown ${drilldownChain.length + 1}`} level="" dimLabel={vLabel} sx={POPOVER_PICKER_SX}
                        used={usedElsewhere(groupBy, drilldownChain, drilldownChain.length)}
                        onChange={(next) => { if (next) setDrilldownChain((prev) => [...prev, next]); }}
                      />
                    )}
                  </Box>
                </Popover>
              </>
            )}
            {!folded && drilldownChain.length < MAX_DRILLDOWN_LEVELS && (adding ? (
              <LevelPicker
                label={drilldownChain.length ? `Drilldown ${drilldownChain.length + 1}` : 'Drilldown'} level="" dimLabel={vLabel}
                sx={PICKER_SX} autoOpen onClose={() => setAdding(false)}
                used={usedElsewhere(groupBy, drilldownChain, drilldownChain.length)}
                onChange={(next) => { if (next) setDrilldownChain((prev) => [...prev, next]); setAdding(false); }}
              />
            ) : (
              <Button size="small" startIcon={<Add />} onClick={() => setAdding(true)} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                Drilldown
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Mobile: the entity filter, then the date, then the action cluster. */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
        {isNarrow && entityFilter}
        {isNarrow && (
          <DateRangePicker
            value={dateRange} onChange={setDateRange}
            footer={<TimezoneSelect value={timezone} onChange={setTimezone} />}
            sx={{ width: '100%' }}
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {actionButtons}
        </Box>
      </Box>

      {/* Row 2 (desktop): date range and entity filter, then the action cluster on the right. */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        {!isNarrow && (
          <DateRangePicker
            value={dateRange} onChange={setDateRange}
            footer={<TimezoneSelect value={timezone} onChange={setTimezone} />}
          />
        )}
        {!isNarrow && entityFilter}
        <Box sx={{ flex: 1 }} />
        {actionButtons}
      </Box>
    </Paper>
  );
}
